import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Square, ChevronRight, Loader2, Save, ArrowLeft, SkipForward } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import MultilingualEditor from "@/components/ui/MultilingualEditor";
import { packMl, hasMlContent } from "@/lib/ml";
import { toast } from "sonner";

const ease = [0.22, 1, 0.36, 1];

// Curated life-story questions, each mapped to a journal chapter.
const QUESTIONS = [
  { q: "What is your earliest memory?", chapter: "childhood" },
  { q: "What was the house you grew up in like?", chapter: "childhood" },
  { q: "What did your mother or father cook best?", chapter: "family" },
  { q: "Who was your best friend at school, and what did you do together?", chapter: "education" },
  { q: "What was your first job, and what did it teach you?", chapter: "work" },
  { q: "How did you meet the person you married — or your dearest love?", chapter: "love" },
  { q: "What is the farthest journey you have ever taken?", chapter: "travel" },
  { q: "Which place in the world feels most like home?", chapter: "home" },
  { q: "Who do you still miss, and what would you tell them today?", chapter: "loss" },
  { q: "What is the hardest thing life has taught you?", chapter: "wisdom" },
  { q: "What should your grandchildren know about the world you grew up in?", chapter: "wisdom" },
  { q: "What family tradition must never be lost?", chapter: "family" },
];

/**
 * /app/interview — Interview Me. Speak an answer; Whisper (whisper-1) writes it
 * down; the transcript lands in a journal composer and saves to My Life.
 */
export default function Interview() {
  const nav = useNavigate();
  const [index, setIndex] = useState(0);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [transcribing, setTranscribing] = useState(false);

  // answer composer
  const [body, setBody] = useState("");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [answered, setAnswered] = useState(0);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const question = QUESTIONS[index];

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm", "audio/mp4"].find((m) => MediaRecorder.isTypeSupported(m));
      if (!mime) {
        toast.error("Recording isn't supported in this browser — try Chrome, Edge or Safari.");
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        await transcribe(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      setRecording(true);
    } catch {
      toast.error("The microphone needs your permission first.");
    }
  }

  function stopRecording() {
    clearInterval(timerRef.current);
    setRecording(false);
    recorderRef.current?.stop();
  }

  async function transcribe(blob) {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, `answer.${blob.type.includes("mp4") ? "mp4" : "webm"}`);
      const { data } = await api.post("/v1/interview/transcribe", fd);
      // Append to any existing draft rather than overwriting it
      setBody((b) => {
        const cur = typeof b === "object" ? b : { en: b || "" };
        const prior = (cur.en || "").trim();
        return { ...cur, en: prior ? `${prior} ${data.text}` : data.text };
      });
      toast.success("Your words are on the page.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTranscribing(false);
    }
  }

  async function saveEntry(e) {
    e.preventDefault();
    if (!hasMlContent(body)) return;
    setSaving(true);
    try {
      await api.post("/v1/journal", {
        title: question.q,
        body: packMl(body),
        entry_date: date.trim() || undefined,
        chapter: question.chapter,
        source: "interview",
      });
      toast.success("Pressed into your book of life.");
      setBody("");
      setDate("");
      setAnswered((n) => n + 1);
      setIndex((i) => (i + 1) % QUESTIONS.length);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mx-auto max-w-3xl px-6 md:px-10 pt-8 pb-24" data-testid="interview-page">
      <Link to="/app/journal" data-testid="interview-back" className="inline-flex items-center gap-2 text-xs text-[color:var(--ink-soft)] link-underline mb-6">
        <ArrowLeft size={12} /> Back to My Life
      </Link>

      <header className="text-center mb-10">
        <div className="overline mb-2">§ Interview Me</div>
        <h1 className="heritage-title text-4xl md:text-6xl leading-tight">Speak, and it is written</h1>
        <p className="mt-3 font-italic-serif italic text-[color:var(--ink-soft)] max-w-lg mx-auto">
          Answer aloud, as if a grandchild were asking. We write it down for you.
        </p>
      </header>

      {/* Question card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.5, ease }}
          className="border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] p-8 md:p-12 text-center shadow-[0_16px_44px_-24px_rgba(43,28,15,0.4)]"
        >
          <div className="numeric-index text-[color:var(--ink-soft)] mb-4">
            Question {index + 1} of {QUESTIONS.length}
          </div>
          <h2 className="font-serif-display text-2xl md:text-4xl leading-snug" data-testid="interview-question">
            {question.q}
          </h2>

          <div className="mt-8 flex items-center justify-center gap-4">
            {!recording ? (
              <button
                data-testid="interview-record"
                onClick={startRecording}
                disabled={transcribing}
                className="btn-wood !py-3 !px-6 disabled:opacity-50"
              >
                <Mic size={16} /> {transcribing ? "Writing it down…" : "Answer aloud"}
              </button>
            ) : (
              <button
                data-testid="interview-stop"
                onClick={stopRecording}
                className="btn-rust !py-3 !px-6 animate-pulse"
              >
                <Square size={16} /> Done · {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
              </button>
            )}
            <button
              data-testid="interview-next"
              onClick={() => setIndex((i) => (i + 1) % QUESTIONS.length)}
              className="btn-ghost !py-2 !px-4 !text-xs"
              disabled={recording}
            >
              <SkipForward size={13} /> Another question
            </button>
          </div>
          {transcribing && (
            <div className="mt-4 text-sm text-[color:var(--ink-soft)]" data-testid="interview-transcribing">
              <Loader2 size={14} className="inline animate-spin mr-2" /> Listening closely…
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Answer composer */}
      <form onSubmit={saveEntry} className="mt-10" data-testid="interview-composer">
        <div className="grid md:grid-cols-[1fr_220px] gap-4 items-start">
          <MultilingualEditor
            as="textarea"
            rows={4}
            testid="interview-body"
            label="Your answer — spoken, then yours to polish"
            value={body}
            onChange={setBody}
            maxLength={4000}
            placeholder="Press 'Answer aloud' and speak… or simply write."
          />
          <div>
            <span className="overline block mb-1">When was this?</span>
            <input
              data-testid="interview-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              placeholder="1978 · optional"
              className="w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-2 focus:outline-none focus:border-[color:var(--rust)]"
            />
          </div>
        </div>
        <button
          type="submit"
          data-testid="interview-save"
          disabled={saving || !hasMlContent(body)}
          className="btn-wood mt-6 disabled:opacity-50"
        >
          <Save size={14} /> {saving ? "Saving…" : "Press into My Life"}
        </button>
      </form>

      {answered > 0 && (
        <div className="mt-10 text-center">
          <p className="font-italic-serif italic text-[color:var(--ink-soft)]">
            {answered} {answered === 1 ? "memory" : "memories"} kept this sitting.
          </p>
          <button onClick={() => nav("/app/journal")} data-testid="interview-view-journal" className="mt-3 btn-ghost !py-2 !px-4 !text-xs">
            Read your book <ChevronRight size={12} />
          </button>
        </div>
      )}
    </section>
  );
}
